import * as React from "react";
import { FlatList, StyleSheet, View } from "react-native";
import { isEmpty, startCase } from "lodash-es";

import {
  Panel,
  PanelBody,
  PanelHeader,
  PanelHeaderTitle,
  Typography,
  VStack,
  useTheme,
} from "@smartrent/ui";

import { useCMWFeatureFlagsQuery } from "@/api/cmw-feature-flags";
import type { Gate } from "@/types";

import { useGroup } from "../GroupContext";

interface FeatureFlagListItemProps {
  item: {
    name: string;
    gate: Gate;
    source: "global" | "property";
  };
  index: number;
}

const FeatureFlagListItem: React.FC<FeatureFlagListItemProps> = ({ item }) => {
  const { colors } = useTheme();
  const styles = makeStyles(colors);

  return (
    <View style={styles.itemContainer}>
      <VStack spacing={4} style={styles.nameContainer}>
        <Typography variation="semibold.body.medium">
          {startCase(item.name)}
        </Typography>
        <Typography
          variation="regular.caption.medium"
          style={styles.sourceText}
          color="textDisabled"
        >
          {item.source === "global" ? "Global" : "Group"}
        </Typography>
      </VStack>
      <View
        style={[
          styles.statusContainerBase,
          item.gate.enabled
            ? styles.enabledContainer
            : styles.disabledContainer,
        ]}
      >
        <Typography
          variation="regular.body.small"
          style={item.gate.enabled ? styles.enabledText : styles.disabledText}
        >
          {item.gate.enabled ? "Enabled" : "Disabled"}
        </Typography>
      </View>
    </View>
  );
};

const FeatureFlagsListEmpty: React.FC = () => {
  const { colors } = useTheme();
  const styles = makeStyles(colors);
  return (
    <View style={styles.emptyContainer}>
      <Typography variation="regular.body.medium" color="textDisabled">
        No enabled feature flags
      </Typography>
    </View>
  );
};

const FeatureFlagsListSeparator: React.FC = () => {
  const { colors } = useTheme();
  const styles = makeStyles(colors);
  return <View style={styles.separator} />;
};

export const GroupFeatureFlagsCard: React.FC = () => {
  const { group } = useGroup();
  const { data, isLoading, error } = useCMWFeatureFlagsQuery();
  const { colors } = useTheme();
  const styles = makeStyles(colors);

  const featureFlags = React.useMemo(() => {
    if (isLoading || error || isEmpty(data) || !data?.length) {
      return [];
    }

    return data.flatMap((flag) =>
      flag.gates
        .filter(
          (gate) =>
            typeof gate.for !== "string" &&
            gate.for.entity === "group" &&
            gate.for.entity_id === Number(group.id)
        )
        .map(
          (gate) =>
            ({
              name: flag.name,
              gate,
              source: "property",
            }) as const
        )
    );
  }, [isLoading, error, data, group.id]);

  return (
    <Panel>
      <PanelHeader>
        <PanelHeaderTitle title="CMW Feature Flags" />
      </PanelHeader>

      <PanelBody>
        <View>
          <FlatList
            key="feature-flags"
            keyExtractor={(item, index) =>
              `feature-flag-${item.name}-${item.source}-${index}`
            }
            testID="feature-flags-list"
            centerContent={true}
            style={styles.flagsList}
            data={featureFlags}
            renderItem={(props) => <FeatureFlagListItem {...props} />}
            ListEmptyComponent={FeatureFlagsListEmpty}
            ItemSeparatorComponent={FeatureFlagsListSeparator}
          />
        </View>
      </PanelBody>
    </Panel>
  );
};

const makeStyles = (colors) =>
  StyleSheet.create({
    flagsList: {
      flexDirection: "column",
      alignContent: "center",
      width: "100%",
      paddingHorizontal: 4,
      paddingBottom: 4,
      paddingTop: 0,
      borderRadius: 0,
      borderTopColor: "transparent",
    },
    itemContainer: {
      padding: 12,
      marginVertical: 4,
      borderRadius: 4,
      backgroundColor: colors.backgroundContrast,
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    nameContainer: {
      flex: 1,
    },
    sourceText: {
      fontStyle: "italic",
    },
    statusContainerBase: {
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 4,
    },
    enabledContainer: {
      backgroundColor: colors.success100,
    },
    disabledContainer: {
      backgroundColor: colors.gray100,
    },
    enabledText: {
      color: colors.success700,
    },
    disabledText: {
      color: colors.gray700,
    },
    emptyContainer: {
      padding: 16,
      alignItems: "center",
    },
    separator: {
      height: 1,
      width: "100%",
      backgroundColor: colors.borderLow,
      marginVertical: 4,
    },
  });
